import { isEmpty } from "lodash";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { cn } from "../../../../lib/utils";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../common/form";
import { Input } from "../../common/input";

interface FormSearchSelectOption {
  id?: string | number;
  title: string;
  value?: string | number;
  disabled?: boolean;
  [key: string]: any;
}

interface FormSearchSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  placeholder?: string;
  required?: boolean;
  description?: string;
  errorMessage?: string;
  options: FormSearchSelectOption[];
  disabled?: boolean;
  loading?: boolean;
  searchByApi?: boolean;
  onSearchChange?: (searchQuery: string) => void;
  customResultRenderer?: (option: FormSearchSelectOption) => React.ReactNode;
  noResultsText?: string;
  onChange?: (value: string) => void;
  className?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
}

export function FormSearchSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  placeholder = "Search...",
  required = false,
  description,
  errorMessage,
  options = [],
  disabled = false,
  loading = false,
  searchByApi = false,
  onSearchChange,
  customResultRenderer,
  noResultsText = "No results found",
  onChange,
  className,
  rules
}: FormSearchSelectProps<TFieldValues, TName>) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter options based on search query
  const filteredOptions = useCallback(() => {
    if (searchByApi) {
      return options;
    }

    if (!searchQuery) {
      return options;
    }

    const query = searchQuery.toLowerCase();
    return options.filter(option => option.title.toLowerCase().includes(query));
  }, [options, searchQuery, searchByApi]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchByApi && onSearchChange) {
      onSearchChange(value);
    }
  };

  const getOptionValue = (option: FormSearchSelectOption) => {
    if (option.value !== undefined && option.value !== null && option.value !== "") {
      return String(option.value);
    }
    if (option.id !== undefined && option.id !== null && option.id !== "") {
      return String(option.id);
    }
    return option.title;
  };

  const getDisplayValue = (fieldValue: any) => {
    if (fieldValue === undefined || fieldValue === null || fieldValue === "") return "";
    const option = options.find(
      opt =>
        getOptionValue(opt) === String(fieldValue) ||
        (opt.id !== undefined && opt.id !== null && String(opt.id) === String(fieldValue))
    );
    return option?.title || "";
  };

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: required ? errorMessage || "This field is required" : false,
        ...rules
      }}
      render={({ field }) => {
        const displayValue = getDisplayValue(field.value);

        return (
          <FormItem className="tw-w-full">
            {!isEmpty(label) ? (
              <FormLabel>
                {label}
                {required && <span className="tw-text-destructive tw-ml-0.5">*</span>}
              </FormLabel>
            ) : null}
            <div ref={dropdownRef} className="tw-relative tw-w-full">
              <FormControl>
                <div
                  className={cn(
                    "tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-1.5 tw-text-sm tw-shadow-xs tw-transition-colors",
                    "focus-within:tw-border-ring focus-within:tw-ring-ring/50 focus-within:tw-ring-[3px]",
                    disabled && "tw-cursor-not-allowed tw-opacity-50",
                    !disabled && "cursor-pointer",
                    className
                  )}
                  onClick={() => {
                    if (!disabled) {
                      setOpen(prev => !prev);
                    }
                  }}
                >
                  <Input
                    value={open ? searchQuery : displayValue}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    onFocus={() => {
                      setOpen(true);
                      setSearchQuery("");
                    }}
                    onClick={e => {
                      // Prevent wrapper toggle from closing the dropdown on first focus/click
                      e.stopPropagation();
                      if (!disabled) {
                        setOpen(true);
                      }
                    }}
                    className="tw-border-0 tw-shadow-none focus-visible:tw-ring-0 tw-h-auto tw-px-2.5 tw-bg-white"
                  />
                  <div className="tw-flex tw-items-center tw-gap-1 tw-pr-2.5">
                    {loading ? (
                      <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin tw-text-muted-foreground" />
                    ) : (
                      <ChevronDown
                        className={cn(
                          "tw-h-4 tw-w-4 tw-text-muted-foreground tw-transition-transform",
                          open && "rotate-180"
                        )}
                      />
                    )}
                  </div>
                </div>
              </FormControl>

              {/* Dropdown */}
              {open && !disabled && (
                <div
                  className={cn(
                    "tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-border tw-bg-popover tw-text-popover-foreground tw-shadow-md",
                    "tw-animate-in tw-fade-in-0 tw-zoom-in-95"
                  )}
                >
                  {loading ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-p-4.5">
                      <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-muted-foreground" />
                    </div>
                  ) : filteredOptions().length === 0 ? (
                    <div className="tw-p-4 tw-text-center tw-text-sm tw-text-muted-foreground">
                      {noResultsText}
                    </div>
                  ) : (
                    <div className="tw-p-1">
                      {filteredOptions().map(option => {
                        const optionValue = getOptionValue(option);
                        const isSelected = String(field.value) === optionValue;
                        const isDisabled = option.disabled;

                        return (
                          <div
                            key={optionValue}
                            className={cn(
                              "tw-relative tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-px-2.5 tw-py-2.5 tw-text-sm tw-outline-none tw-transition-colors",
                              isDisabled
                                ? "tw-cursor-not-allowed tw-opacity-50"
                                : "hover:tw-bg-primary/15 hover:tw-text-foreground",
                              isSelected && "tw-bg-accent/50"
                            )}
                            onClick={() => {
                              if (!isDisabled) {
                                field.onChange(optionValue);
                                onChange?.(optionValue);
                                setOpen(false);
                                setSearchQuery("");
                              }
                            }}
                          >
                            {isSelected && (
                              <Check className="tw-mr-2 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-primary" />
                            )}
                            <div className={cn("tw-flex-1", isSelected && "tw-ml-0")}>
                              {customResultRenderer ? (
                                customResultRenderer(option)
                              ) : (
                                <span>{option.title}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {description && <p className="tw-text-sm tw-text-muted-foreground">{description}</p>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
